import "./contentstack-page.scss";

import React, {
  useContext,
  useEffect
} from "react";

import { GlobalNavigation } from "design-system/navigation/global-navigation/GlobalNavigation";
import CategoryPage from "design-system/page-templates/category-page/CategoryPage";
import CorePage from "design-system/page-templates/core-page/CorePage";
import LandingPage from "design-system/page-templates/landing-page/LandingPage";
import ManualsPage from "design-system/page-templates/manuals-page/ManualsPage";
import NewsArticle from "design-system/page-templates/news-article/NewsArticle";
import Page from "design-system/page-templates/page/Page";
import PlanDetails from "design-system/page-templates/plan-details/PlanDetails";
import GlobalFooter from "design-system/shared/components/global-footer/GlobalFooter";
import SectionFooter from "design-system/shared/components/section-footer/SectionFooter";
import Seo from "design-system/shared/components/seo/Seo";
import { AppDataContext } from "design-system/shared/contexts/app-data/AppDataContext";
import {
  AppDataModel,
  CorePageContent,
  LandingPageContent,
  NewsReleaseContent,
  PageContent,
  PhGlobalFooterNav,
  PhMainNavigation,
  PhManuals,
  PhSiteSettings,
  PlanDetailsContent
} from "design-system/shared/models/app-data-model";
import { graphql } from "gatsby";
import useFeatureToggle from "design-system/shared/hooks/use-feature-toggle/useFeatureToggle";

export type ContentStackPageProps = {
  data: ContentStackPageQueryResponse;
  pageContext: any;
};

export type ContentStackPageQueryResponse = {
  phCorepage: CorePageContent;
  phMainNavigation: PhMainNavigation;
  phPage: PageContent;
  phNewsReleases: NewsReleaseContent;
  phLandingPage: LandingPageContent;
  allPhContentCategories: any;
  phManuals: PhManuals;
  phSiteSettings: PhSiteSettings;
  phGlobalFooterNav: PhGlobalFooterNav;
  phPlanDetails: PlanDetailsContent;
  phSubNavigationMenus: any;
};

const ContentStackPage: React.FC<ContentStackPageProps> = (props: ContentStackPageProps) => {
  const context = useContext(AppDataContext);

  useEffect(() => {
    if (context.setAppData) {
      const appData: AppDataModel = {
        ...context.appData
      };

      appData.main_navigation = props.data?.phMainNavigation;
      appData.globalSettings = props.data?.phSiteSettings;

      context.setAppData(appData);
    }
    // we can't depend on context or we endless loop!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phSiteSettings = props.data?.phSiteSettings;
  const globalAlert = phSiteSettings?.site_alert;
  const featureToggles = phSiteSettings?.feature_toggles;
  const loginDropdownItems = phSiteSettings?.login_dropdown_items;

  let coreSubNav = props.data.phSubNavigationMenus;

  // Core SubNav Overrides
  if (props.pageContext.isCorepage && props.data.phCorepage?.core_subnav[0]) {
    coreSubNav = props.data.phCorepage?.core_subnav[0];
  }

  if (props.pageContext.isPage && props.data.phPage.page_hierarchy?.core_site[0]?.core_subnav[0]) {
    coreSubNav = props.data.phPage.page_hierarchy?.core_site[0]?.core_subnav[0];
  }

  if (props.pageContext.isPlanDetailsPage && props.data.phPlanDetails.page_hierarchy?.core_site[0]?.core_subnav[0]) {
    coreSubNav = props.data.phPlanDetails.page_hierarchy?.core_site[0]?.core_subnav[0];
  }

  if (props.pageContext.isManualPage && props.data.phManuals.page_hierarchy?.core_site[0]?.core_subnav[0]) {
    coreSubNav = props.data.phManuals.page_hierarchy?.core_site[0]?.core_subnav[0];
  }

  // Site Footer Overrides
  let sectionFooter = undefined;

  if (props.pageContext.isCorepage && props.data.phCorepage?.section_footer[0]) {
    sectionFooter = props.data.phCorepage.section_footer[0];
  }

  if (props.pageContext.isPage && props.data.phPage.page_hierarchy?.core_site[0]?.section_footer[0]) {
    sectionFooter = props.data.phPage.page_hierarchy?.core_site[0]?.section_footer[0];
  }

  if (props.pageContext.isPlanDetailsPage && props.data.phPlanDetails.page_hierarchy?.core_site[0]?.section_footer[0]) {
    sectionFooter = props.data.phPlanDetails.page_hierarchy?.core_site[0]?.section_footer[0];
  }

  if (props.pageContext.isManualPage && props.data.phManuals.page_hierarchy?.core_site[0]?.section_footer[0]) {
    sectionFooter = props.data.phManuals.page_hierarchy?.core_site[0]?.section_footer[0];
  }

  const isSlfsvc220On = useFeatureToggle('SLFSVC-220', featureToggles);

  const rendering = (
    <>
      <div className={isSlfsvc220On ? 'isSlfsvc220On' : ''}></div>
      <GlobalNavigation mainNavLinks={props.data.phMainNavigation.main_navigation_group} subNav={coreSubNav} globalAlert={globalAlert} featureToggles={featureToggles} loginDropdownItems={loginDropdownItems} />
      {props.data.phCorepage && <CorePage content={props.data.phCorepage} featureToggles={featureToggles} phSiteSettings={phSiteSettings} />}
      {props.data.phPage && <Page content={props.data.phPage} featureToggles={featureToggles} phSiteSettings={phSiteSettings} />}
      {props.data.phNewsReleases && <NewsArticle content={props.data.phNewsReleases} featureToggles={featureToggles} phSiteSettings={phSiteSettings} />}
      {props.pageContext.newsReleasesByCategory && <CategoryPage content={props.pageContext.newsReleasesByCategory} categoryUrl={props.pageContext.url} categoryTitle={props.pageContext.title} limit={props.pageContext.limit}  skip={props.pageContext.skip} numPages={props.pageContext.numPages} currentPage={props.pageContext.currentPage} pageUrl={props.pageContext.baseUrl} featureToggles={featureToggles} />}
      {props.data.phLandingPage && <LandingPage content={props.data.phLandingPage} featureToggles={featureToggles} phSiteSettings={phSiteSettings} />}
      {props.data.phManuals && <ManualsPage content={props.data.phManuals} latestPhNews={props.pageContext.latestPhNewsReleases} featureToggles={featureToggles} />}
      {props.data.phPlanDetails && <PlanDetails content={props.data.phPlanDetails} featureToggles={featureToggles} phSiteSettings={phSiteSettings} />}
      {sectionFooter && <SectionFooter sectionFooter={sectionFooter} featureToggles={featureToggles} />}
      {props.data.phGlobalFooterNav && <GlobalFooter phGlobalFooterNav={props.data.phGlobalFooterNav} featureToggles={featureToggles} />}
    </>
  );

  return rendering;
};

export const ContentStackPageQuery = graphql`
  query (
    $id: String
    $isCorepage: Boolean! = false
    $isPage: Boolean! = false
    $isNewsReleasePage: Boolean! = false
    $isLandingPage: Boolean! = false
    $isManualPage: Boolean! = false
    $isPlanDetailsPage: Boolean! = false    
  ) {
    phMainNavigation {
      ...globalNavFragment
    }
    phSubNavigationMenus(uid: {eq: "bltb89e101ae900c86c"}) {
      ...globalSubNavigationMenusFragment
    }
    phSiteSettings {
      ...siteSettings
    }
    phGlobalFooterNav {
      ...globalFooterFragment
    }
    phCorepage(id: { eq: $id }) @include(if: $isCorepage) {
      ...corePageFragment
    }
    phPage(id: { eq: $id }) @include(if: $isPage) {
      ...pageFragment
    }
    phNewsReleases(id: {eq: $id}) @include(if: $isNewsReleasePage) {
      ...newsReleasesFragment
    }
    phLandingPage(id: { eq: $id }) @include(if: $isLandingPage) {
      ...landingPageFragment
    }
    phManuals(id: { eq: $id }) @include(if: $isManualPage) {
      ...manualsPageFragment
    }
    phPlanDetails(id: { eq: $id }) @include(if: $isPlanDetailsPage) {
      ...planDetailsFragment
    }
  }`;

export const Head = (props: ContentStackPageProps) => {
  return (
    <html lang="en">
      {props.pageContext.seo && <Seo content={props.pageContext.seo} />}
    </html>
  );
};
export default ContentStackPage;
